import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generates a professional PDF invoice or quotation for Promotional LLC-FZ
 * @param {Object} invoice - The invoice/order data from the backend
 * @param {String} docType - "Invoice" or "Quotation"
 */
export const generateInvoicePDF = (invoice, docType = "Invoice") => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const isPaid = invoice.paymentStatus === 'Paid' || invoice.status === 'Paid';

    // -- Header & Branding --
    try {
        doc.addImage('/IMG_5468.PNG', 'PNG', 20, 10, 45, 12);
    } catch (e) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(110, 60, 180);
        doc.text("Promotional", 20, 22);
        doc.setTextColor(180, 180, 180);
        doc.text("LLC-FZ", 66, 22);
    }

    // Company Details
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(80, 80, 80);
    const companyInfo = [
        "www.promotional.ae | info@promotional.ae",
        "052-3622578 | Opal Tower, Business Bay, Dubai, U.A.E.",
        "TRN: 104101080000003"
    ];
    companyInfo.forEach((line, i) => {
        doc.text(line, 20, 26 + (i * 3.5));
    });

    // Invoice Title & ID (Right Side)
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    const title = docType === "Quotation" ? "QUOTATION" : "TAX INVOICE";
    doc.text(title, pageWidth - 20, 20, { align: "right" });
    doc.setFontSize(9);
    doc.text(`# ${invoice.orderId}`, pageWidth - 20, 26, { align: "right" });

    // Balance Due / PAID Box (Right Side)
    if (isPaid && docType === "Invoice") {
        doc.setFillColor(220, 252, 231);
        doc.roundedRect(pageWidth - 58, 30, 38, 10, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(22, 163, 74);
        doc.text("✓ PAID", pageWidth - 39, 37, { align: "center" });
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
    } else {
        doc.setFillColor(240, 240, 240);
        doc.roundedRect(pageWidth - 58, 30, 38, 10, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text(docType === "Quotation" ? "Total Amount" : "Balance Due", pageWidth - 39, 35, { align: "center" });
        doc.setFontSize(10);
        doc.text(`AED ${invoice.amount.toFixed(2)}`, pageWidth - 39, 39, { align: "center" });
        doc.setFontSize(9);
    }

    // Divider line
    doc.setDrawColor(220, 220, 220);
    doc.line(20, 43, pageWidth - 20, 43);

    // -- Bill To & Dates (compact side-by-side layout) --
    let billY = 48;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(80, 80, 80);
    doc.text("BILL TO", 20, billY);
    billY += 4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(invoice.customer || "Walking Customer", 20, billY);
    billY += 3.5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(60, 60, 60);

    // Address (Issue #4 - always show address)
    const address = invoice.shippingAddress || invoice.customerAddress || "";
    if (address) {
        const addressLines = doc.splitTextToSize(`Address: ${address}`, 80);
        addressLines.forEach(line => {
            doc.text(line, 20, billY);
            billY += 3.2;
        });
    }
    if (invoice.customerPhone) { doc.text(`Phone: ${invoice.customerPhone}`, 20, billY); billY += 3.2; }
    if (invoice.customerEmail) { doc.text(`Email: ${invoice.customerEmail}`, 20, billY); billY += 3.2; }
    if (invoice.taxTreatment) { doc.text(`Tax Treatment: ${invoice.taxTreatment}`, 20, billY); billY += 3.2; }
    if (invoice.trn) { doc.text(`TRN: ${invoice.trn}`, 20, billY); billY += 3.2; }
    if (invoice.placeOfSupply) { doc.text(`Place of Supply: ${invoice.placeOfSupply}`, 20, billY); }

    // Dates (Right Side)
    const dateColX = pageWidth - 65;
    const valColX = pageWidth - 20;
    let dateY = 48;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);

    const addDateRow = (label, value) => {
        doc.text(label, dateColX, dateY);
        doc.text(value, valColX, dateY, { align: "right" });
        dateY += 4;
    };

    addDateRow(docType === "Quotation" ? "Date :" : "Invoice Date :", new Date(invoice.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }));
    addDateRow("Terms :", invoice.paymentTerms || "Due on Receipt");
    if (docType === "Invoice") {
        addDateRow("Due Date :", invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "N/A");
        if (invoice.paymentMethod) addDateRow("Payment Mode :", invoice.paymentMethod);
    }

    // -- Items Table (Issue #6 - optimized for 8+ items, no image column) --
    const tableStartY = Math.max(billY + 4, dateY + 4, 72);

    const tableData = invoice.items.map((item, index) => [
        index + 1,
        item.description ? `${item.product}\n${item.description}` : item.product,
        item.quantity.toFixed(2),
        item.price.toFixed(2),
        ((item.quantity * item.price) - (item.discount || 0)).toFixed(2),
        `${(item.tax || 0).toFixed(1)}%`,
        item.total.toFixed(2)
    ]);

    autoTable(doc, {
        startY: tableStartY,
        head: [['#', 'Item & Description', 'Qty', 'Rate', 'Taxable Amt', 'Tax', 'Amount']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [40, 40, 40], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, cellPadding: 1.5 },
        columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 13, halign: 'center' },
            3: { cellWidth: 18, halign: 'right' },
            4: { cellWidth: 22, halign: 'right' },
            5: { cellWidth: 14, halign: 'right' },
            6: { cellWidth: 20, halign: 'right' }
        },
        styles: { fontSize: 7, verticalAlign: 'middle', cellPadding: 1.0 },
        alternateRowStyles: { fillColor: [252, 252, 252] }
    });

    // -- Totals --
    const tableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : tableStartY + 40;
    let finalY = tableEndY + 5;
    const lblX = pageWidth - 75;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(60, 60, 60);
    doc.text("Sub Total", lblX, finalY);
    doc.text((invoice.amount - (invoice.tax || 0)).toFixed(2), valColX, finalY, { align: "right" });

    finalY += 4;
    doc.text("VAT", lblX, finalY);
    doc.text((invoice.tax || 0).toFixed(2), valColX, finalY, { align: "right" });

    finalY += 4;
    doc.setDrawColor(180, 180, 180);
    doc.line(lblX, finalY, pageWidth - 20, finalY);
    finalY += 4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(0, 0, 0);
    doc.text("Total", lblX, finalY);
    doc.text(`AED ${invoice.amount.toFixed(2)}`, valColX, finalY, { align: "right" });

    if (docType === "Invoice") {
        finalY += 5;
        if (isPaid) {
            // Issue #5 - Show PAID instead of Balance Due
            doc.setFillColor(220, 252, 231);
            doc.roundedRect(lblX - 2, finalY - 4, pageWidth - 20 - lblX + 2, 7, 1, 1, "F");
            doc.setFont("helvetica", "bold");
            doc.setTextColor(22, 163, 74);
            doc.text("PAID", lblX, finalY);
            doc.text("AED 0.00", valColX, finalY, { align: "right" });
            doc.setTextColor(0, 0, 0);
        } else {
            doc.setFillColor(245, 245, 245);
            doc.roundedRect(lblX - 2, finalY - 4, pageWidth - 20 - lblX + 2, 7, 1, 1, "F");
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 0, 0);
            doc.text("Balance Due", lblX, finalY);
            doc.text(`AED ${invoice.amount.toFixed(2)}`, valColX, finalY, { align: "right" });
        }
    }

    // -- Tax Summary --
    finalY += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Tax Summary", 20, finalY);

    const taxRateStr = invoice.taxTreatment === "Non VAT Registered" ? "0.00%" : "5.00%";
    autoTable(doc, {
        startY: finalY + 3,
        head: [['Tax Details', 'Taxable Amount (AED)', 'Tax Amount (AED)']],
        body: [[`Standard Rate (${taxRateStr})`, (invoice.amount - (invoice.tax || 0)).toFixed(2), (invoice.tax || 0).toFixed(2)]],
        theme: 'plain',
        headStyles: { fillColor: [40, 40, 40], textColor: [255, 255, 255], fontSize: 7 },
        tableWidth: 100,
        styles: { fontSize: 7.5 }
    });

    // -- Notes & Bank Details --
    const taxTableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : finalY + 15;
    finalY = taxTableEndY + 6;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Notes", 20, finalY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const notes = invoice.customerNotes || "Looking forward for your business.";
    doc.text(notes, 20, finalY + 4);

    finalY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Bank Account Details:", 20, finalY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text("Account Name: Promotional L.L.C-FZ  |  Bank: WIO Bank", 20, finalY + 4);

    // Footer
    const pageH = doc.internal.pageSize.height;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, pageH - 35, pageWidth - 20, pageH - 35);

    let footerY = pageH - 30;
    doc.setFontSize(7.5);
    doc.setTextColor(60, 60, 60);
    doc.text("Account Currency: AED", 20, footerY);
    doc.text("IBAN: AE490860000009562434370", 20, footerY + 4);
    doc.text("BIC/SWIFT: WIOBAEADXXX  |  Account No: 9562434370", 20, footerY + 8);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    doc.text("Terms & Conditions", 115, footerY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    const terms = [
        "1. 100% advance payment required.",
        "2. 5-7 working days for delivery after payment and design approval.",
        "3. Price given for normal size logo (5cm approx).",
        "4. Payment via payment link incurs 3% extra charge on total amount."
    ];
    terms.forEach((term, idx) => { doc.text(term, 115, footerY + 5 + (idx * 3)); });

    doc.save(`${docType}_${invoice.orderId}.pdf`);
};
